import { z } from 'zod'

import {
  expandConditionsToFlatSets,
  findReadonlyFilterFields,
  normalizeEnumCondition,
  pickEditableStatusField,
} from '../filter-normalizer'
import {
  resolveDataClass,
  unknownEntityError,
} from '../shared/entity-serializer'
import { encodeFilterQuery } from '../shared/filterQuery'
import { isQueryValidationError } from '../shared/orderArg'
import { resolveRelation } from '../shared/resolveRelation'
import type { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder/types'
import type { AdapterSchemaAttributes } from '@/Atomic/Widgets/AiChatBot/core/engine/contracts'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'

export const countEntitiesTool = defineTool({
  name: 'count_entities',
  description:
    'Return the true total count of CRM entities. ' +
    'For top-level counts ("how many companies?") supply dataClass + optional filters. ' +
    'For related-entity counts ("how many activities does company X have?") supply parentDataClass + parentId + relation — the tool resolves junction tables automatically. ' +
    'Never count rows from list_entities (it is limit-capped and will produce wrong totals).',
  inputSchema: () =>
    z.object({
      dataClass: z
        .string()
        .optional()
        .describe(
          'Entity type to count, e.g. Company, Person, Ticket, Activity. Required for top-level counts; omit when using parentDataClass + relation.',
        ),
      filters: z
        .record(z.string(), z.unknown())
        .optional()
        .describe(
          'Filter key-value pairs for top-level counts. Do NOT use this for relations — use parentDataClass + parentId + relation instead.',
        ),
      searchTerm: z
        .string()
        .optional()
        .describe('Optional keyword to scope the count to matching records'),
      parentDataClass: z
        .string()
        .optional()
        .describe(
          'Parent entity type when counting related records, e.g. Company. Provide together with parentId and relation.',
        ),
      parentId: z
        .string()
        .optional()
        .describe('The _id of the parent record when counting related records'),
      relation: z
        .string()
        .optional()
        .describe(
          'Relation name as defined in the registry, e.g. activities, persons, tickets',
        ),
    }),

  async run(args, ctx) {
    const {
      dataClass,
      filters = {},
      searchTerm,
      parentDataClass,
      parentId,
      relation,
    } = args

    // --- Related-entity count via registry (handles junctions automatically) ---
    if (parentDataClass && parentId && relation) {
      const resolved = resolveRelation(
        parentDataClass,
        parentId,
        relation,
        ctx.featureFlags,
      )
      if ('error' in resolved) {
        return resolved
      }

      const { data } = getHostRuntime()
      if (resolved.kind === 'junction') {
        // Counts raw junction rows. For symmetric junctions (ContactContactRelation)
        // the parent can appear on both sides, so this may exceed the distinct
        // related-record count get_related returns (which dedupes by _id).
        const count = await data.countResolved(resolved.jdc, {
          filters: resolved.junctionFilters,
        })
        return {
          dataClass: String(resolved.targetDataClass),
          parentDataClass,
          relation,
          count,
        }
      }

      const count = await data.countResolved(resolved.dc, {
        filters: resolved.filters,
      })
      return {
        dataClass: String(resolved.targetDataClass),
        parentDataClass,
        relation,
        count,
      }
    }

    // --- Top-level count ---
    if (!dataClass) {
      return {
        error:
          'Either dataClass or (parentDataClass + parentId + relation) is required.',
      }
    }

    if (!resolveDataClass(dataClass)) {
      return unknownEntityError(dataClass)
    }

    const { data } = getHostRuntime()

    // Load the schema once when there is a filter to validate — reused for both
    // the readonly guard below and the enum label→code resolution after it.
    let schemaAttributes: AdapterSchemaAttributes | undefined
    if (Object.keys(filters).length > 0) {
      try {
        const result = await data.getSchema(data.getSchemaName(dataClass))
        schemaAttributes = result.schemaAttributes
      } catch {
        schemaAttributes = undefined
      }
    }

    // Reject a filter on a computed/readonly field (e.g. Opportunity `open`)
    // before it silently corrupts the count. On the flat channel PISA ignores an
    // unknown/computed filter and counts EVERY record (open:true → 2472), which
    // the model then reports as the "open" total — a fabrication. Same
    // schema-driven guard as list_entities/build_filter.
    if (schemaAttributes) {
      const readonlyBad = findReadonlyFilterFields(
        Object.keys(filters),
        schemaAttributes,
      )
      if (readonlyBad.length > 0) {
        const editableStatusField = pickEditableStatusField(schemaAttributes)
        return {
          error:
            `Cannot count ${dataClass} by ${readonlyBad
              .map((f) => `"${f}"`)
              .join(
                ', ',
              )}: computed/readonly field(s) the backend cannot filter on — it would count ALL records, not the matching ones.` +
            (editableStatusField
              ? ` To count by state (open, active, won, etc.), use the editable "${editableStatusField}" field with a specific value instead.`
              : ''),
        }
      }
    }

    // Resolve enum display labels ("in progress", status-group names) to PISA
    // codes, exactly as list_entities does. The model passes labels, not codes —
    // an unresolved label would be counted verbatim (0 matches) and silently
    // misreported as the real total. A single group label can expand to several
    // codes (returns an array), forcing the structured/flat-sum channel below.
    // Uses the same schema-driven normalizer, so label→code has one source of
    // truth across count_entities and list_entities.
    const effectiveFilters: Record<string, unknown> = {}
    const filterErrors: string[] = []
    for (const [field, value] of Object.entries(filters)) {
      const attr = schemaAttributes?.[field]
      if (attr?.type === 'enum' && attr.enumValues?.length) {
        const normalized = normalizeEnumCondition(
          Array.isArray(value) ? 'in' : 'eq',
          value,
          attr.enumValues,
        )
        const validCodes = new Set(attr.enumValues.map((e) => e.value))
        const resolvedValues = Array.isArray(normalized.value)
          ? (normalized.value as unknown[])
          : normalized.value !== null
            ? [normalized.value]
            : []
        const unresolved = resolvedValues
          .map(String)
          .filter((v) => !validCodes.has(v))
        if (unresolved.length > 0) {
          const validLabels = [...new Set(attr.enumValues.map((e) => e.title))]
          filterErrors.push(
            `Field "${field}" ("${attr.title}"): ${unresolved
              .map((v) => `"${v}"`)
              .join(', ')} did not match any value. Valid options: ${validLabels
              .map((l) => `"${l}"`)
              .join(', ')}.`,
          )
          continue
        }
        effectiveFilters[field] = normalized.value
      } else {
        effectiveFilters[field] = value
      }
    }

    if (filterErrors.length > 0) {
      return {
        error:
          `Cannot count ${dataClass}. ${filterErrors.join(' ')} ` +
          'Present this to the user and ask which they mean — do NOT report a ' +
          'count of 0 as if it were the real total.',
      }
    }

    // Use the structured ?query= channel when any filter value is an array
    // (e.g. multi-value enum like status). String(array) would produce a
    // comma-joined string that PISA rejects as a single invalid enum value.
    const hasArrayFilter = Object.values(effectiveFilters).some(Array.isArray)
    let count: number | null

    if (hasArrayFilter) {
      const conditions = Object.entries(effectiveFilters).map(
        ([field, value]) => ({
          field,
          op: Array.isArray(value) ? 'in' : 'eq',
          value,
        }),
      )
      const structure: SavedFilterStructure = { or: [{ and: conditions }] }
      try {
        count = await data.count(dataClass, {
          encodedFilterQuery: encodeFilterQuery(structure),
          ...(searchTerm ? { search: searchTerm } : {}),
        })
      } catch (err) {
        // The structured `?query=` channel is unavailable on some instances — it
        // 422s on every structured query, even a well-formed one. `.count()`
        // surfaces that as a wrapped DataScopeError ("A validation has failed").
        // Sum per-value flat counts instead — correct for a single multi-valued
        // enum like `status` (a record has exactly one status, so the per-code
        // counts are disjoint). Mirrors list_entities' flat fallback. Rethrow
        // anything else (transient blips, real query errors) rather than masking it.
        if (!isQueryValidationError(err)) {
          throw err
        }
        const flatSets = expandConditionsToFlatSets(conditions, 25)
        if (!flatSets) {
          return {
            error:
              `Cannot count ${dataClass}: this instance does not support advanced (structured) filtering, and the filter has too many value combinations to count on the basic channel. ` +
              'Ask the user to narrow the criteria.',
          }
        }
        const counts = await Promise.all(
          flatSets.map((flat) =>
            data.count(dataClass, {
              filters: Object.fromEntries(
                Object.entries(flat).map(([k, v]) => [k, String(v)]),
              ),
              ...(searchTerm ? { search: searchTerm } : {}),
            }),
          ),
        )
        count = counts.reduce<number>((sum, c) => sum + (c ?? 0), 0)
      }
    } else {
      const stringFilters = Object.fromEntries(
        Object.entries(effectiveFilters).map(([k, v]) => [k, String(v)]),
      )
      count = await data.count(dataClass, {
        filters: stringFilters,
        ...(searchTerm ? { search: searchTerm } : {}),
      })
    }

    return { dataClass, count }
  },
})
